import React, { useContext, useEffect } from 'react';

import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { isEmpty, isNil, isNull } from 'lodash';
import Markdown from 'markdown-to-jsx';
import { useTrackerInstanceSectionTemplateResources } from 'sdk';

import EmptyDataState from 'pages/Teachers/Insights/components/EmptyDataState';
import LoadingSkeleton from 'pages/Teachers/Insights/components/LoadingSkeleton';
import LoadingState from 'pages/Teachers/Insights/components/LoadingState';
import MasteryLevelGroup from 'pages/Teachers/Insights/components/MasteryLevelGroup';
import ProgressBar from 'pages/Teachers/Insights/components/ProgressBar';
import {
  CELEBRATE,
  CELEBRATE_COLOR,
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  INTERVENE,
  INTERVENE_COLOR,
  OVERALL_RESPONSE_CELEBRATE_COLLAPSIBLE,
  OVERALL_RESPONSE_INTERVENE_COLLAPSIBLE,
  OVERALL_RESPONSE_SUPPORT_COLLAPSIBLE,
  PENDING,
  PENDING_COLOR,
  SIGNIFICANT_ERRORS_IN_WORK,
  SUPPORT,
  SUPPORT_COLOR,
  UNABLE_TO_DIAGNOSE,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'pages/Teachers/Insights/constants';
import {
  trackMixpanelEvent,
  trackMixpanelPageView
} from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';

import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import { ReactComponent as CelebrateIcon } from './assets/celebrate-icon.svg';
import { ReactComponent as InterveneIcon } from './assets/intervene-icon.svg';
import { ReactComponent as PendingIcon } from './assets/pending-icon.svg';
import { ReactComponent as SupportIcon } from './assets/support-icon.svg';
import styles from './styles.module.scss';

const OverallResponseOverview = ({
  data,
  isLoading,
  refetchOverview,
  trackerInstanceSectionId,
  latestOverallResponseMixpanelData,
  setLatestOverallResponseMixpanelData,
  fullyPreAnnotated,
  hasSubmittedStudentWorks
}) => {
  const user = useContext(UserContext);

  const { data: templateResources, isLoading: isTemplateResourcesLoading } =
    useTrackerInstanceSectionTemplateResources({
      trackerInstanceSectionId
    });

  useEffect(() => {
    trackMixpanelPageView(user.id, {
      feature: 'insights',
      view: 'overall response',
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
    });
  }, [user]);

  const trackOverallResponseMasteryLevelGroupExpand = ({ groupName }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'insights',
        view: 'overall response',
        button: groupName,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };

  const trackOverallResponseStudentWorkDialogClick = ({ groupName }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'insights',
        view: 'overall response',
        button: 'student work',
        group: groupName,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };

  const trackOverallResponseNextStepsMarkAsCompleted = ({
    completedNextStep
  }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
      {
        feature: 'insights',
        view: 'overall response',
        button: 'complete',
        next_step: completedNextStep,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };
  useEffect(() => {
    if (isLoading || isNil(data)) {
      return;
    } else {
      if (
        isNull(latestOverallResponseMixpanelData) ||
        latestOverallResponseMixpanelData['celebrate'] !==
          data['celebrate_data']['students'].length ||
        latestOverallResponseMixpanelData['support'] !==
          data['support_data']['students'].length ||
        latestOverallResponseMixpanelData['intervene'] !==
          data['intervene_data']['students'].length ||
        latestOverallResponseMixpanelData['pending'] !==
          data['no_strategy_data']['students'].length
      ) {
        trackMixpanelEvent(user.id, 'Students grouped by overall response', {
          feature: 'insights',
          view: 'overall response',
          celebrate: data['celebrate_data']['students'].length,
          support: data['support_data']['students'].length,
          intervene: data['intervene_data']['students'].length,
          pending: data['no_strategy_data']['students'].length,
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
        });

        setLatestOverallResponseMixpanelData({
          celebrate: data['celebrate_data']['students'].length,
          support: data['support_data']['students'].length,
          intervene: data['intervene_data']['students'].length,
          pending: data['no_strategy_data']['students'].length
        });
      }
    }
  }, [
    user,
    data,
    isLoading,
    latestOverallResponseMixpanelData,
    setLatestOverallResponseMixpanelData
  ]);

  if (isLoading && isNil(data)) return <LoadingSkeleton />;
  if (
    hasSubmittedStudentWorks &&
    hasSubmittedStudentWorks['has_submitted_student_works'] === false
  ) {
    return <EmptyDataState withoutStudentWorks={true} />;
  }

  const {
    celebrate_data: {
      student_percentage: celebratePercentage,
      students: celebrateStudents,
      next_steps: celebrateNextSteps,
      exemplar_urls: celebrateExemplarUrls
    },
    support_data: {
      student_percentage: supportPercentage,
      students: supportStudents,
      next_steps: supportNextSteps,
      exemplar_urls: supportExemplarUrls
    },
    intervene_data: {
      student_percentage: intervenePercentage,
      students: interveneStudents,
      next_steps: interveneNextSteps,
      exemplar_urls: interveneExemplarUrls
    },
    no_strategy_data: {
      student_percentage: noStrategyPercentage,
      students: noStrategyStudents,
      next_steps: noStrategyNextSteps,
      exemplar_urls: noStrategyExemplarUrls
    }
  } = data;

  const celebratePercentageParsed = parseFloat(celebratePercentage);
  const supportPercentageParsed = parseFloat(supportPercentage);
  const intervenePercentageParsed = parseFloat(intervenePercentage);
  const noStrategyPercentageParsed = parseFloat(noStrategyPercentage);

  const hasNoData =
    celebratePercentageParsed === 0 &&
    supportPercentageParsed === 0 &&
    intervenePercentageParsed === 0 &&
    noStrategyPercentageParsed === 0;

  if (!isLoading && hasNoData && fullyPreAnnotated) {
    return <EmptyDataState />;
  }

  if (!isLoading && hasNoData) {
    return <LoadingState />;
  }

  return (
    <div className={styles.container}>
      <Typography variant="H-TEXT-2" style={{ textTransform: 'none' }}>
        To help you get started, we&apos;ve grouped your students by
        mastery—those excelling (Celebrate), those progressing (Support), and
        those needing targeted support (Intervention). Our AI helps get you
        started, but tweak groups as needed to celebrate progress and reteach
        effectively with our intervention strategies.
      </Typography>
      <ProgressBar
        celebratePercentage={celebratePercentage}
        supportPercentage={supportPercentage}
        intervenePercentage={intervenePercentage}
        noStrategyPercentage={noStrategyPercentage}
      />
      {!isTemplateResourcesLoading &&
        (!isNil(templateResources['responding_to_student_thinking']) ||
          !isNil(templateResources['teacher_guide'])) && (
          <div className={styles.resourcesContainer}>
            <Typography variant="B-Text-1" className={styles.resourcesLabel}>
              {!isNil(templateResources['responding_to_student_thinking']) && (
                <Markdown>
                  {templateResources['responding_to_student_thinking']}
                </Markdown>
              )}
            </Typography>
            {!isNil(templateResources['teacher_guide']) && (
              <a
                href={templateResources['teacher_guide']}
                target="_blank"
                rel="noreferrer"
                className={styles.resourceLink}
              >
                Teacher Guide
              </a>
            )}
          </div>
        )}
      <div data-intercom-target={OVERALL_RESPONSE_CELEBRATE_COLLAPSIBLE}>
        <MasteryLevelGroup
          title={CELEBRATE}
          icon={<CelebrateIcon />}
          students={celebrateStudents}
          percentage={celebratePercentage}
          nextSteps={celebrateNextSteps}
          exemplarUrls={celebrateExemplarUrls}
          color={CELEBRATE_COLOR}
          description={WORK_IS_COMPLETE_AND_CORRECT}
          refetchOverview={refetchOverview}
          trackGroupExpandEvent={() =>
            trackOverallResponseMasteryLevelGroupExpand({
              groupName: CELEBRATE
            })
          }
          trackStudentWorkDialogClickEvent={() =>
            trackOverallResponseStudentWorkDialogClick({ groupName: CELEBRATE })
          }
          trackNextStepCompleteEvent={
            trackOverallResponseNextStepsMarkAsCompleted
          }
        />
      </div>
      <div data-intercom-target={OVERALL_RESPONSE_SUPPORT_COLLAPSIBLE}>
        <MasteryLevelGroup
          title={SUPPORT}
          icon={<SupportIcon />}
          students={supportStudents}
          percentage={supportPercentage}
          nextSteps={supportNextSteps}
          exemplarUrls={supportExemplarUrls}
          color={SUPPORT_COLOR}
          description={GENERAL_CONCEPTUAL_UNDERSTANDING}
          refetchOverview={refetchOverview}
          trackGroupExpandEvent={() =>
            trackOverallResponseMasteryLevelGroupExpand({ groupName: SUPPORT })
          }
          trackStudentWorkDialogClickEvent={() =>
            trackOverallResponseStudentWorkDialogClick({ groupName: SUPPORT })
          }
          trackNextStepCompleteEvent={
            trackOverallResponseNextStepsMarkAsCompleted
          }
        />
      </div>
      <div data-intercom-target={OVERALL_RESPONSE_INTERVENE_COLLAPSIBLE}>
        <MasteryLevelGroup
          title={INTERVENE}
          icon={<InterveneIcon />}
          students={interveneStudents}
          percentage={intervenePercentage}
          nextSteps={interveneNextSteps}
          exemplarUrls={interveneExemplarUrls}
          color={INTERVENE_COLOR}
          description={SIGNIFICANT_ERRORS_IN_WORK}
          refetchOverview={refetchOverview}
          trackGroupExpandEvent={() =>
            trackOverallResponseMasteryLevelGroupExpand({
              groupName: INTERVENE
            })
          }
          trackStudentWorkDialogClickEvent={() =>
            trackOverallResponseStudentWorkDialogClick({ groupName: INTERVENE })
          }
          trackNextStepCompleteEvent={
            trackOverallResponseNextStepsMarkAsCompleted
          }
        />
      </div>
      {!isEmpty(noStrategyStudents) && (
        <div>
          <MasteryLevelGroup
            title={PENDING}
            icon={<PendingIcon />}
            students={noStrategyStudents}
            percentage={noStrategyPercentage}
            nextSteps={noStrategyNextSteps}
            exemplarUrls={noStrategyExemplarUrls}
            color={PENDING_COLOR}
            description={UNABLE_TO_DIAGNOSE}
            refetchOverview={refetchOverview}
            trackGroupExpandEvent={() =>
              trackOverallResponseMasteryLevelGroupExpand({
                groupName: PENDING
              })
            }
            trackStudentWorkDialogClickEvent={() =>
              trackOverallResponseStudentWorkDialogClick({
                groupName: PENDING
              })
            }
            trackNextStepCompleteEvent={
              trackOverallResponseNextStepsMarkAsCompleted
            }
          />
        </div>
      )}
    </div>
  );
};

export default OverallResponseOverview;
